import { motion } from "framer-motion";
import React, { useState, useEffect, useRef } from "react";
import { welcomeMessage, getRandomSuggestions } from '../utils/chatConstants';
import { loadMessages, saveMessages, loadVisitorName, saveVisitorName, loadChuckMode, saveChuckMode } from '../utils/storageUtils';
import { handleCommand } from './Commands';
import {
    handleSubmit,
    handleSuggestionClick,
    simulateTyping,
    skipTyping,
    clearChat,
    handleNameEdit,
    handleNameSubmit
} from '../utils/chatFunctions';
import Header from './Terminal/Header';
import ChatWindow from './Terminal/ChatWindow';
import InputArea from './Terminal/InputArea';
import Suggestions from './Terminal/Suggestions';

const componentMap = {
    Skills: React.lazy(() => import('./SpecialOutput/Skills')),
    Projects: React.lazy(() => import('./SpecialOutput/Projects')),
    Timeline: React.lazy(() => import('./SpecialOutput/Timeline')),
    About: React.lazy(() => import('./SpecialOutput/About')),
    Socials: React.lazy(() => import('./SpecialOutput/Socials')),
    AnimeList: React.lazy(() => import('./SpecialOutput/AnimeList')),
    MangaList: React.lazy(() => import('./SpecialOutput/MangaList')),
    TelnetFrame: React.lazy(() => import('./SpecialOutput/TelnetFrame')),
    Hobbies: React.lazy(() => import('./SpecialOutput/Hobbies')),
    ArtStation: React.lazy(() => import('./SpecialOutput/ArtStation')),
};

export default function AIChat() {
    const [messages, setMessages] = useState(loadMessages);
    const [visitorName, setVisitorName] = useState(loadVisitorName);
    const [isEditingName, setIsEditingName] = useState(false);
    const nameInputRef = useRef(null);
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const chatRef = useRef(null);
    const [currentSuggestions, setCurrentSuggestions] = useState([]);
    const typingIntervalRef = useRef(null);
    const [isChuckMode, setIsChuckMode] = useState(loadChuckMode);

    useEffect(() => {
        saveMessages(messages);
    }, [messages]);

    useEffect(() => {
        saveVisitorName(visitorName);
    }, [visitorName]);

    useEffect(() => {
        if (messages.length === 0) {
            setMessages([welcomeMessage]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = (e) => handleSubmit(e, input, setInput, setMessages, setCurrentSuggestions, handleCommand, setIsChuckMode);
    const onSuggestionClick = (suggestion) => handleSuggestionClick(suggestion, setMessages, setInput, setCurrentSuggestions);
    const onNameEdit = () => handleNameEdit(setIsEditingName, nameInputRef);
    const onNameSubmit = (e) => handleNameSubmit(e, nameInputRef, setVisitorName, setIsEditingName);
    const onClearChat = () => {
        clearChat(welcomeMessage, setMessages);
        setIsChuckMode(false);
        saveChuckMode(false);
    };

    useEffect(() => {
        setCurrentSuggestions(getRandomSuggestions());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (messages.length === 0) return;
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.sender === "ai" && lastMessage.displayText === "") {
            simulateTyping(lastMessage.id, lastMessage.text, setMessages, setIsTyping, typingIntervalRef);
        }
    }, [messages]);

    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [messages]);

    const renderComponent = (componentName) => {
        if (!componentName || !componentMap[componentName]) return null;

        const Component = componentMap[componentName];
        return (
            <React.Suspense fallback={<div className="text-primary/60">Loading...</div>}>
                <Component />
            </React.Suspense>
        );
    };

    return (
        <motion.section
            id="chat"
            className="min-h-screen w-full md:w-[80vw] mx-auto flex items-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
        >
            <div className="h-screen sm:h-[80vh] w-full">
                <div className="min-h-full w-full bg-cyber-black/90 backdrop-blur-md overflow-hidden border border-white/10 opacity-90 shadow-[0_0_15px_rgba(0,255,0,0.2),0_0_30px_rgba(255,0,255,0.1),0_0_50px_rgba(0,255,0,0.1)] rounded-md">
                    <Header
                        visitorName={visitorName}
                        isEditingName={isEditingName}
                        onNameEdit={onNameEdit}
                        onNameSubmit={onNameSubmit}
                        nameInputRef={nameInputRef}
                        onClearChat={onClearChat}
                    />

                    <ChatWindow
                        chatRef={chatRef}
                        messages={messages}
                        visitorName={visitorName}
                        renderComponent={renderComponent}
                        isChuckMode={isChuckMode}
                    />

                    <InputArea
                        visitorName={visitorName}
                        input={input}
                        setInput={setInput}
                        onSubmit={onSubmit}
                        onClearChat={onClearChat}
                        isTyping={isTyping}
                    />

                    <Suggestions
                        currentSuggestions={currentSuggestions}
                        onSuggestionClick={onSuggestionClick}
                        isTyping={isTyping}
                        skipTyping={skipTyping}
                        typingIntervalRef={typingIntervalRef}
                        messages={messages}
                        setMessages={setMessages}
                        setIsTyping={setIsTyping}
                    />
                </div>
            </div>
        </motion.section>
    );
}
